import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, SlidersHorizontal, RotateCcw } from 'lucide-react';

import api from '../../services/api';
import AsyncGenericSelect from '../ui/AsyncGenericSelect';
import Button from '../ui/Button';
import GenericFormDrawer from '../GenericFormDrawer';
import { formatDateTime } from '../../utils/Utils';
import { nameLookup } from './PmAnalyticsFilterBar';
import { ORG_VIEWS, EMPTY_FILTERS } from './pmAnalyticsFilters';

// PM analitik P6 — çok dönemli (E11 trendler) ve dönem + tarih aralıklı (E13 geri bildirim) yüzeylerin F0 süzgeç şeridi.
// PmAnalyticsFilterBar ile AYNI süzgeç sözleşmesini ve URL durumunu (pmAnalyticsFilters) kullanır; farkı, dönem denetimini
// çağıranın vermesi (periodControl — E11'de çoklu dönem, E13'te tek dönem) ve karşılaştırma dönemi / katılımcı durumu
// süzgeçlerinin olmamasıdır (bu yüzeyler daima aktif katılımcıyla hesaplar). Etkin süzgeçler silinebilir etiketlerdir.
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

export const Field = styled.div`
  width: ${({ $w }) => $w || '220px'};
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme, $tone }) => ($tone === 'warning' ? theme.colors.auxiliar.warning : theme.colors.surface.border)};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const ChipRemove = styled(Button)`
  min-height: 0;
  padding: 0;
  line-height: 0;
`;

const rows = (res) => { const r = res?.data?.data; return Array.isArray(r) ? r : (r?.data || []); };

export const employeeLookup = {
  onSearch: async (q) => {
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return rows(res).map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() }));
  },
  onGetById: async (id) => {
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res?.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() };
    } catch {
      return { id, name: '' };
    }
  },
};

const SCOPE_KEYS = ['departmentIds', 'locationIds', 'managerEmployeeId', 'titleIds', 'groupIds', 'orgView'];

/** Etkin F0 anahtarları (dönem, karşılaştırma ve katılımcı durumu hariç). */
export function activeScopeKeys(filters) {
  const f = { ...EMPTY_FILTERS, ...filters };
  return SCOPE_KEYS.filter((k) => {
    if (k === 'orgView') return f.orgView !== 'snapshot';
    if (k === 'managerEmployeeId') return !!f.managerEmployeeId;
    return Array.isArray(f[k]) && f[k].length > 0;
  });
}

export function clearScopeKey(filters, key) {
  const next = { ...filters, [key]: EMPTY_FILTERS[key] };
  if (key === 'managerEmployeeId') next.includeDescendants = true;
  return next;
}

export default function PmScopeFilterBar({
  filters, onChange, onReset, periodControl, extraControls, extraChips = [], lastUpdated, stale,
}) {
  const { t, i18n } = useTranslation();
  // Çekmecenin başlangıç değerleri açıldığı anda donar (PmAnalyticsFilterBar dersi: yeniden çizimde form sıfırlanmasın).
  const [drawerInitial, setDrawerInitial] = useState(null);
  const departments = useMemo(() => nameLookup('/v1/departments'), []);
  const locations = useMemo(() => nameLookup('/v1/locations'), []);
  const titles = useMemo(() => nameLookup('/v1/titles'), []);
  const groups = useMemo(() => nameLookup('/v1/user-groups'), []);
  const open = () => setDrawerInitial({
    managerEmployeeId: filters.managerEmployeeId,
    includeDescendants: filters.includeDescendants,
    titleIds: filters.titleIds,
    groupIds: filters.groupIds,
    orgView: filters.orgView,
  });

  const schema = [
    { name: 'managerEmployeeId', label: t('performance.operations.filter.manager'), type: 'asyncSelect', grid: 12, ...employeeLookup },
    { name: 'includeDescendants', label: t('performance.operations.filter.includeDescendants'), type: 'checkbox', grid: 12 },
    { name: 'titleIds', label: t('performance.operations.filter.titles'), type: 'asyncSelect', isMulti: true, grid: 12, ...titles },
    { name: 'groupIds', label: t('performance.operations.filter.groups'), type: 'asyncSelect', isMulti: true, grid: 12, ...groups },
    {
      name: 'orgView', label: t('performance.operations.filter.orgView'), type: 'select', grid: 12,
      options: ORG_VIEWS.map((v) => ({ id: v, name: t(`performance.operations.orgView.${v}`) })), idKey: 'id', labelKey: 'name',
    },
  ];

  const chipLabel = (key) => {
    if (key === 'orgView') return t(`performance.operations.orgView.${filters.orgView}`);
    const count = Array.isArray(filters[key]) ? filters[key].length : null;
    const base = t(`performance.operations.filter.chip.${key}`);
    return count != null ? `${base} (${count})` : base;
  };
  const active = activeScopeKeys(filters);

  return (
    <>
      <Bar>
        {periodControl}
        <Field>
          <AsyncGenericSelect
            label={t('performance.operations.filter.departments')}
            value={filters.departmentIds}
            onChange={(v) => onChange({ ...filters, departmentIds: v || [] })}
            isMulti
            minChars={0}
            compact
            {...departments}
          />
        </Field>
        <Field>
          <AsyncGenericSelect
            label={t('performance.operations.filter.locations')}
            value={filters.locationIds}
            onChange={(v) => onChange({ ...filters, locationIds: v || [] })}
            isMulti
            minChars={0}
            compact
            {...locations}
          />
        </Field>
        {extraControls}
        <Button variant="outline" onClick={open} data-action="more-filters">
          <SlidersHorizontal size={16} />
          {' '}
          {t('performance.operations.filter.more')}
        </Button>
        <Meta>
          {lastUpdated ? <span data-meta="updated">{t('performance.operations.filter.lastUpdated', { time: formatDateTime(lastUpdated, i18n.language) })}</span> : null}
          {stale ? <Tag $tone="warning" data-meta="stale">{t('performance.operations.filter.stale')}</Tag> : null}
          {filters.orgView === 'current' ? <Tag $tone="warning" data-meta="current-org">{t('performance.operations.filter.currentOrgNotice')}</Tag> : null}
          {filters.groupIds.length ? <Tag data-meta="group-current">{t('performance.operations.filter.groupCurrent')}</Tag> : null}
        </Meta>
      </Bar>
      {active.length > 0 || extraChips.length > 0 ? (
        <Chips>
          {active.map((key) => (
            <Tag key={key} data-chip={key}>
              {chipLabel(key)}
              <ChipRemove variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={() => onChange(clearScopeKey(filters, key))}>
                <X size={12} />
              </ChipRemove>
            </Tag>
          ))}
          {extraChips.map((chip) => (
            <Tag key={chip.key} data-chip={chip.key}>
              {chip.label}
              {chip.onRemove ? (
                <ChipRemove variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={chip.onRemove}>
                  <X size={12} />
                </ChipRemove>
              ) : null}
            </Tag>
          ))}
          {onReset ? (
            <Button variant="ghost" onClick={onReset} data-action="reset">
              <RotateCcw size={14} />
              {' '}
              {t('performance.operations.filter.reset')}
            </Button>
          ) : null}
        </Chips>
      ) : null}
      <GenericFormDrawer
        open={drawerInitial !== null}
        onClose={() => setDrawerInitial(null)}
        title={t('performance.operations.filter.more')}
        schema={schema}
        initialValues={drawerInitial || undefined}
        submitLabel={t('performance.operations.filter.apply')}
        onSave={async (values) => {
          onChange({
            ...filters,
            managerEmployeeId: values.managerEmployeeId || null,
            includeDescendants: values.includeDescendants !== false,
            titleIds: values.titleIds || [],
            groupIds: values.groupIds || [],
            orgView: values.orgView || 'snapshot',
          });
          return true;
        }}
      />
    </>
  );
}
